import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { NetworkConfig, NETWORKS, getNetworkByChainId, getSupportedNetworks } from '../config/networks';

interface NetworkContextType {
  currentNetwork: NetworkConfig | null;
  supportedNetworks: NetworkConfig[];
  switchNetwork: (networkKey: string) => Promise<void>;
  addNetwork: (network: NetworkConfig) => Promise<void>;
  isNetworkSupported: (chainId: number) => boolean;
  isLoading: boolean;
  error: string | null;
  setCurrentNetworkFromChainId: (chainId: number) => void;
}

const NetworkContext = createContext<NetworkContextType | undefined>(undefined);

interface NetworkProviderProps {
  children: ReactNode;
  walletProvider?: any; // The provider from WalletContext
}

export const NetworkProvider: React.FC<NetworkProviderProps> = ({ children, walletProvider }) => {
  const [currentNetwork, setCurrentNetwork] = useState<NetworkConfig | null>(null);
  const [supportedNetworks, setSupportedNetworks] = useState<NetworkConfig[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize supported networks
  useEffect(() => {
    const networks = getSupportedNetworks();
    setSupportedNetworks(networks);
  }, []);

  const setCurrentNetworkFromChainId = useCallback((chainId: number): void => {
    const network = getNetworkByChainId(chainId);
    if (network && network.enabled) {
      setCurrentNetwork(network);
      setError(null);
    } else {
      setError(`Unsupported network with chain ID: ${chainId}`);
    }
  }, []);

  // Detect network when wallet provider changes
  useEffect(() => {
    if (walletProvider) {
      // Freighter / Stellar providers do not speak EIP-1193
      if (walletProvider.__stellar) {
        setCurrentNetwork(null);
        setError(null);
        return;
      }

      const detectNetwork = async () => {
        try {
          const chainId = await walletProvider.request({ method: 'eth_chainId' });
          const parsedChainId = parseInt(chainId, 16);
          setCurrentNetworkFromChainId(parsedChainId);
        } catch (err) {
          console.warn('Failed to detect network:', err);
        }
      };
      
      detectNetwork();

      // Listen for network changes
      const handleChainChanged = (chainId: string) => {
        const parsedChainId = parseInt(chainId, 16);
        setCurrentNetworkFromChainId(parsedChainId);
      };

      if (typeof walletProvider.on === 'function') {
        walletProvider.on('chainChanged', handleChainChanged);
      }

      return () => {
        if (typeof walletProvider.removeListener === 'function') {
          walletProvider.removeListener('chainChanged', handleChainChanged);
        }
      };
    } else {
      setCurrentNetwork(null);
    }
  }, [walletProvider, setCurrentNetworkFromChainId]);

  const switchNetwork = async (networkKey: string): Promise<void> => {
    const network = NETWORKS[networkKey];
    if (!network) {
      throw new Error(`Network ${networkKey} not found`);
    }

    // Use the provider that was passed from WalletContext
    if (!walletProvider) {
      throw new Error('No wallet provider available. Please connect your wallet first.');
    }

    setIsLoading(true);
    setError(null);

    try {
      // Try to switch to the network
      await walletProvider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${network.chainId.toString(16)}` }],
      });
      setCurrentNetwork(network);
    } catch (switchError: any) {
      // If the network doesn't exist, try to add it
      if (switchError.code === 4902) {
        await addNetwork(network);
      } else {
        throw switchError;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const addNetwork = async (network: NetworkConfig): Promise<void> => {
    // Use the provider that was passed from WalletContext
    if (!walletProvider) {
      throw new Error('No wallet provider available. Please connect your wallet first.');
    }

    try {
      await walletProvider.request({
        method: 'wallet_addEthereumChain',
        params: [{
          chainId: `0x${network.chainId.toString(16)}`,
          chainName: network.name,
          rpcUrls: [network.rpcUrl],
          blockExplorerUrls: [network.blockExplorer],
          nativeCurrency: network.nativeCurrency,
        }],
      });
      setCurrentNetwork(network);
    } catch (error) {
      throw new Error(`Failed to add network: ${(error as Error).message}`);
    }
  };

  const isNetworkSupported = (chainId: number): boolean => {
    return supportedNetworks.some(network => network.chainId === chainId);
  };

  const value: NetworkContextType = {
    currentNetwork,
    supportedNetworks,
    switchNetwork,
    addNetwork,
    isNetworkSupported,
    isLoading,
    error,
    setCurrentNetworkFromChainId,
  };

  return (
    <NetworkContext.Provider value={value}>
      {children}
    </NetworkContext.Provider>
  );
};

export const useNetwork = (): NetworkContextType => {
  const context = useContext(NetworkContext);
  if (context === undefined) {
    throw new Error('useNetwork must be used within a NetworkProvider');
  }
  return context;
};
